import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AwaitModules, Button, ComponentSync, LL_H_C, LL_V_L, openContent, Show, stopPropagation, TS_CollapsableContainerV2 } from '@nu-art/thunderstorm-frontend/index';
import './Component_MetapathwayOrderCart.scss';
import { AdvisorConfigKeyFE_HomePanel, ModuleFE_Advisor_Action, ModuleFE_Advisor_ColorGroup, ModuleFE_Advisor_OrderGroup, ModuleFE_Advisor_Pathway, ModuleFE_Advisor_Variable, ModuleFE_EMInterface, ModuleFE_RuntimeOrder, ModuleFE_RuntimeProtocol } from '@app/advisor-frontend';
import { RuntimeOrderStatus_Enum, } from '@app/advisor-shared';
import { FadeScrollWrapper, ICONS } from '@app/styles';
import { ModuleFE_ProtocolInteraction } from '../../../../_enum.js';
import { _values, dbObjectToId, Debounce, exists, filterDuplicates, sortArray, ThisShouldNotHappenException, voidFunction } from '@nu-art/ts-common';
import { getMetapathwayActions } from '../../../../_utils/get-metapathway-actions.js';
import { Component_MetaPathwayOrder_Group } from '../metapathway-order/Component_MetaPathwayOrder_Group.js';
import { Component_MetaPathwayOrder_General } from '../metapathway-order/Component_MetaPathwayOrder_General.js';
import { iconTooltipId } from '../../../_style/icon-tooltip/index.js';
import { PopUp_OrderGroupsResolver } from '../../../pop-ups/PopUp_OrderGroupsResolver/PopUp_OrderGroupsResolver.js';
import { ModuleFE_AdvisorAnalytics } from '../../../../_analytics/consts.js';
import { OrderPlacedSource } from '../../../../_analytics/types.js';
import { ProtocolData } from '../../../../_utils/ProtocolData/ProtocolData.js';
import { Toaster, ToasterPortal_BottomUp } from '@nu-art/toasting';
import { Component_AdvisorSL_OrderCart_Body, Component_AdvisorSL_OrderCart_Header } from '../../../loaders/skeleton/order-cart/Component_AdvisorSL_OrderCart.js';
const fallback_Title = 'Suggested Workup';
const fallback_Subtitle = 'Additional recommended orders';
const toastKey_OrderCart = 'toast-key__order-cart';
const toastFilter = (model) => model.key === toastKey_OrderCart;
const modulesToAwait = [
    //Static Modules
    ModuleFE_Advisor_Pathway,
    ModuleFE_Advisor_Action,
    ModuleFE_Advisor_OrderGroup,
    ModuleFE_Advisor_ColorGroup,
    ModuleFE_Advisor_Variable,
    //Runtime Modules
    ModuleFE_RuntimeProtocol,
    ModuleFE_RuntimeOrder,
];
class Component_MetapathwayOrderCart_Class extends ComponentSync {
    updateActionsDebouncer = new Debounce(() => this.updateActions(), 300);
    toaster = new Toaster(toastKey_OrderCart, 1600);
    //######################### Life Cycle #########################
    __onReadOnlyChanged = () => {
        this.setState({ disabled: ModuleFE_EMInterface.isReadonly() });
    };
    __onRuntimeProtocolsUpdated = (...params) => this.updateActionsDebouncer.trigger();
    __onRuntimeOrdersUpdated = (...params) => this.updateActionsDebouncer.trigger();
    deriveStateFromProps(nextProps, state) {
        state.metapathwayActions ??= getMetapathwayActions();
        state.collapsed ??= true;
        state.disabled = ModuleFE_EMInterface.isReadonly();
        state.config ??= AdvisorConfigKeyFE_HomePanel.get()?.sections?.cart;
        return state;
    }
    //######################### Logic #########################
    updateActions = async () => {
        const metapathwayActions = getMetapathwayActions();
        //Remove all actions that are no longer relevant
        this.updateActions_RemoveInProgress(metapathwayActions);
        //Update existing actions with protocol associations
        this.updateActions_UpdateExisting(metapathwayActions);
        //Force UI to display changes
        this.forceUpdate();
        //Add new actions with animation
        await this.updateActions_AddNewRecommended(metapathwayActions);
    };
    updateActions_RemoveInProgress = (metapathwayActions) => {
        const existingOrderIds = metapathwayActions.orders.map(order => order.rtOrder._id);
        const existingGroupIds = metapathwayActions.groups.map(group => group.groupId);
        this.state.metapathwayActions.orders = this.state.metapathwayActions.orders.filter(order => existingOrderIds.includes(order.rtOrder._id));
        this.state.metapathwayActions.groups = this.state.metapathwayActions.groups.filter(group => existingGroupIds.includes(group.groupId));
    };
    updateActions_UpdateExisting = (metapathwayActions) => {
        this.state.metapathwayActions.orders = this.state.metapathwayActions.orders.map(order => metapathwayActions.orders.find(i => i.rtOrder._id === order.rtOrder._id));
        this.state.metapathwayActions.groups = this.state.metapathwayActions.groups.map(group => metapathwayActions.groups.find(i => i.groupId === group.groupId));
    };
    updateActions_AddNewRecommended = async (metapathwayActions) => {
        const delta = (metapathwayActions.orders.length - this.state.metapathwayActions.orders.length)
            + (metapathwayActions.groups.length - this.state.metapathwayActions.groups.length);
        if (!delta)
            return;
        this.toaster.toast('order-cart', {
            body: _jsxs(_Fragment, { children: [_jsx("span", { className: 'order-count', children: delta }), " ", `New ${delta === 1 ? 'order' : 'orders'} has been added to metapathway actions`] }),
        });
        //Delay update UI
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                this.setState({ metapathwayActions }, () => resolve());
            }, 1800);
        });
    };
    openAddOrder = () => {
        const generalProtocolId = ModuleFE_RuntimeProtocol.getGeneralProtocol()?._id;
        if (!generalProtocolId)
            return;
        return ModuleFE_ProtocolInteraction.openAddOrder(generalProtocolId);
    };
    toggleCollapse = (e) => {
        e.stopPropagation();
        const nextCollapse = !this.state.collapsed;
        const orderCount = this.getActionCount();
        if (!nextCollapse) {
            ModuleFE_AdvisorAnalytics.trigger.view.view__suggestedWorkup();
            ModuleFE_AdvisorAnalytics.trigger.orderCart.orderCart_open({ orderCount });
        }
        else {
            ModuleFE_AdvisorAnalytics.trigger.orderCart.orderCart_abandon({ orderCount });
        }
        this.setState({ collapsed: nextCollapse });
    };
    getActionCount = () => this.state.metapathwayActions.groups.length + this.state.metapathwayActions.orders.length;
    orderAll = async (e) => {
        stopPropagation(e);
        const groupIds = this.state.metapathwayActions.groups.map(order => order.groupId);
        const rtOrders = this.state.metapathwayActions.orders.map(order => order.rtOrder).filter(rtOrder => rtOrder.orderStatus === RuntimeOrderStatus_Enum.RECOMMENDED);
        // const rtOrderIds = rtOrders.map(dbObjectToId)
        PopUp_OrderGroupsResolver.show(e, {
            groupIds,
            pickedRTOrders: rtOrders,
            limitToExistingRecommendedOrders: true,
            onSubmit: async (orderIdMapper) => {
                this.logInfo('orderIdMapper', orderIdMapper);
                const resolvedRTOrders = _values(orderIdMapper).map(orderId => {
                    const rtOrder = ModuleFE_RuntimeOrder.cache.find(rtOrder => rtOrder.orderId === orderId && rtOrder.orderStatus === RuntimeOrderStatus_Enum.RECOMMENDED);
                    if (!rtOrder)
                        throw new ThisShouldNotHappenException(`Could not find RTOrder for order id ${orderId}`);
                    return rtOrder;
                });
                const placed = filterDuplicates([...rtOrders, ...resolvedRTOrders]);
                const rtOrderIds = placed.map(dbObjectToId);
                const orderAmount = await ModuleFE_RuntimeOrder.orderAll(rtOrderIds, groupIds);
                if (orderAmount > 0)
                    placed.forEach(rtOrder => {
                        const dbOrder = ModuleFE_Advisor_Action.cache.unique(rtOrder.orderId);
                        ModuleFE_AdvisorAnalytics.trigger.orders.order_placed({
                            orderId: rtOrder.orderId,
                            orderLabel: dbOrder?.label ?? dbOrder?.name ?? 'N/A',
                            source: OrderPlacedSource.suggestedWorkup,
                        });
                    });
                ModuleFE_AdvisorAnalytics.trigger.navigation.navigation_OrderAll({
                    context: 'metapathway',
                    orderAmount: orderAmount,
                });
                ModuleFE_AdvisorAnalytics.trigger.orderCart.orderCart_submit({ orderCount: orderAmount });
                this.setState({ collapsed: true });
            }
        });
    };
    getSortedActions = () => {
        const protocolIndexMap = {};
        const allActions = [
            ...this.state.metapathwayActions.groups,
            ...this.state.metapathwayActions.orders,
        ];
        //Sort orders by their lowest protocol sorting index
        return sortArray(allActions, action => {
            let lowestIndex = Infinity;
            action.protocols.forEach(p => {
                if (!exists(protocolIndexMap[p._id])) {
                    const data = ProtocolData.createNew(p._id);
                    const index = data?.getLowestSortingIndex();
                    if (!exists(index))
                        return;
                    protocolIndexMap[p._id] = index;
                }
                if (protocolIndexMap[p._id] < lowestIndex)
                    lowestIndex = protocolIndexMap[p._id];
            });
            return lowestIndex;
        });
    };
    //######################### Render #########################
    render() {
        return _jsx(TS_CollapsableContainerV2, { className: 'metapathway-order-cart', headerRenderer: this.render_Header, containerRenderer: this.render_Body, customCaret: () => undefined, collapsed: this.state.collapsed, onCollapseToggle: voidFunction, animated: true, forceUpdate: true });
    }
    render_Header = () => {
        const title = this.state.config?.title ?? fallback_Title;
        const subtitle = this.state.config?.subtitle ?? fallback_Subtitle;
        const actionCount = this.getActionCount();
        return _jsxs(_Fragment, { children: [_jsxs(LL_V_L, { className: 'order-cart-header', children: [_jsxs(LL_H_C, { className: 'order-cart-header__title-wrapper', children: [_jsx("div", { className: 'order-cart-header__title', children: title }), _jsx("div", { className: 'order-cart-header__count', children: actionCount })] }), exists(subtitle) && _jsx("div", { className: 'order-cart-header__subtitle', children: subtitle })] }), _jsxs(Show, { children: [_jsx(Show.If, { condition: this.state.collapsed, children: _jsx(Button, { variant: 'primary', onClick: this.toggleCollapse, children: "View" }) }), _jsx(Show.Else, { children: _jsx(ICONS.advisor_avn_close.component, { onClick: this.toggleCollapse }) })] }), _jsx(ToasterPortal_BottomUp, { height: 100, bottomOffset: 8, modelFilter: toastFilter })] });
    };
    render_Body = () => {
        const sortedActions = this.getSortedActions();
        ModuleFE_AdvisorAnalytics.trigger.uiLoad.uiLoad_OrderCart({
            elapsed_ms: ModuleFE_AdvisorAnalytics.getTimeSinceLoad(),
        });
        return _jsxs(LL_V_L, { className: 'order-cart-body', children: [_jsx(FadeScrollWrapper, { children: _jsx(LL_V_L, { className: 'order-cart-orders', children: sortedActions.map(action => {
                            if ('groupId' in action) {
                                return _jsx(Component_MetaPathwayOrder_Group, { groupId: action.groupId, rtOrders: action.runtimeOrders, relatedProtocols: action.protocols }, action.groupId);
                            }
                            else if ('rtOrder' in action) {
                                return _jsx(Component_MetaPathwayOrder_General, { rtOrder: action.rtOrder, relatedProtocols: action.protocols }, action.rtOrder._id);
                            }
                            else {
                                return _jsx(_Fragment, {});
                            }
                        }) }) }), _jsxs(LL_H_C, { className: 'order-cart-body-actions', children: [this.render_AddOrder(), this.render_OrderAll()] })] });
    };
    render_AddOrder = () => {
        return _jsxs(Button, { variant: 'text', ...openContent.tooltip.left(iconTooltipId, 'Add order', { offset: 8 }), onClick: this.openAddOrder, children: [_jsx(ICONS.advisor_avn_add.component, {}), " Add Order"] });
    };
    render_OrderAll = () => {
        const actionCount = this.getActionCount();
        return _jsx(Button, { variant: 'secondary', onClick: this.orderAll, disabled: !actionCount, ...openContent.tooltip.left(iconTooltipId, 'Send all orders in tray to Epic', { offset: 10 }), children: "Order All" });
    };
}
export const Component_MetapathwayOrderCart = () => _jsx(AwaitModules, { Component: () => _jsx(Component_MetapathwayOrderCart_Class, {}), modules: modulesToAwait, customLoader: () => _jsx(TS_CollapsableContainerV2, { className: 'metapathway-order-cart', customCaret: () => undefined, animated: true, headerRenderer: () => {
            const config = AdvisorConfigKeyFE_HomePanel.get()?.sections?.cart;
            return _jsx(Component_AdvisorSL_OrderCart_Header, { config: config });
        }, containerRenderer: _jsx(Component_AdvisorSL_OrderCart_Body, {}) }) });
//# sourceMappingURL=Component_MetapathwayOrderCart.js.map